import { useCallback, useEffect, useState } from "react";
import { Center, Loader } from "@mantine/core";
import { Route, Routes } from "react-router-dom";
import { setDataSource, staticSource } from "../data";
import { setScenario, type Scenario } from "../data/staticSource";
import { useAuthStore } from "../stores/authStore";
import { useConsoleStore } from "../stores/consoleStore";
import { useQueryStore } from "../stores/queryStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { BaseProvider } from "../components/ConsoleShell";
import { DevToolbar } from "../components/DevToolbar";
import ConsoleRoutes from "./ConsoleRoutes";
import SignIn from "../pages/SignIn";

/**
 * The /dev mount. Same stores, same screens, same shell — only the data source
 * and the session differ. The toolbar reseeds the static store so every empty
 * state and every palette can be checked against the mockup in a browser.
 */
export default function DevRoot() {
  const enterDevMode = useAuthStore((s) => s.enterDevMode);
  const devSession = useAuthStore((s) => s.devSession);
  const loadWorkspaces = useWorkspaceStore((s) => s.load);
  const resetConsole = useConsoleStore((s) => s.resetAll);
  const resetQuery = useQueryStore((s) => s.reset);
  const resetWorkspaces = useWorkspaceStore((s) => s.reset);
  const [ready, setReady] = useState(false);
  const [epoch, setEpoch] = useState(0);

  // Arriving from the live console is both an identity and a data-source
  // switch, so it clears the caches exactly like a sign-out does. Without this,
  // the real tenant's rows stay on screen under the fixtures and a live request
  // still in flight — a createKey and its one-time secret above all — resolves
  // into the dev session. Resetting BEFORE `ready` flips is what keeps it safe:
  // ConsoleRoutes is still behind the loader, so no child loader has fired yet.
  useEffect(() => {
    resetConsole();
    resetQuery();
    resetWorkspaces();
    setDataSource(staticSource);
    enterDevMode();
    setReady(true);
  }, [enterDevMode, resetConsole, resetQuery, resetWorkspaces]);

  useEffect(() => {
    if (ready) void loadWorkspaces();
  }, [ready, epoch, loadWorkspaces]);

  // Reseeding must clear every cached collection, or the previous scenario's
  // rows stay on screen until each store happens to reload.
  const onScenario = useCallback(
    (scenario: Scenario) => {
      setScenario(scenario);
      resetConsole();
      resetQuery();
      resetWorkspaces();
      setEpoch((n) => n + 1);
    },
    [resetConsole, resetQuery, resetWorkspaces],
  );

  if (!ready || !devSession) {
    return (
      <Center h="100vh" bg="var(--vf-bg)">
        <Loader color="var(--vf-acc)" />
      </Center>
    );
  }

  return (
    <BaseProvider base="/dev">
      <Routes>
        {/* The sign-in screen is a design surface too — reachable at /dev/signin. */}
        <Route
          path="signin"
          element={<SignIn onDevSignIn={() => { window.location.href = "/dev/dashboard"; }} />}
        />
        <Route
          path="*"
          element={
            <ConsoleRoutes key={epoch} devBar={<DevToolbar onScenario={onScenario} />} />
          }
        />
      </Routes>
    </BaseProvider>
  );
}
